import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { defaultWallets } from 'modules/ConnectPage/components/connect-section/config';

import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { ROUTES } from 'shared/constants';
import { useConnect } from 'shared/hooks/use-web3';
import { useUIStore } from 'shared/store/uiStore';
import { useWeb3ClientsStore } from 'shared/store/web3-clients-store';
import { StackName } from 'shared/store/web3-clients-store';
import { detectStack } from 'shared/web3/utils/detectStack';

import CloseIcon from 'assets/close-x.svg?react';
import wallet from 'assets/png/wallet.png?format=webp;avif&as=picture';

interface IConnectWalletProps {
  title: string;
  description: string;
}

export const ConnectWallet = ({ title, description }: IConnectWalletProps) => {
  const { t } = useTranslation();
  const { connect, connectors, isPending } = useConnect();
  const navigate = useNavigate();
  const { connectWalletModal, setConnectWalletModalOpen } = useUIStore();
  const { stack, setStack } = useWeb3ClientsStore();
  const { login, isAuthenticated, isPrivyLoading } = useWeb3ClientsStore();

  useLayoutEffect(() => {
    if (isPending && !isAuthenticated) {
      setStack(StackName.WAGMI);
    }
  }, [isPending, setStack, isAuthenticated]);

  // When user chooses Privy, defer opening the Privy modal until provider is mounted
  useEffect(() => {
    if (
      stack === StackName.PRIVY &&
      typeof login === 'function' &&
      !isAuthenticated &&
      !isPrivyLoading
    ) {
      login();
    }
  }, [stack, login, isAuthenticated]);

  const handleNavigateHome = useCallback(() => {
    if (stack === StackName.PRIVY) {
      setStack(detectStack());
    }
    navigate(ROUTES.HOME);
    if (connectWalletModal.show) {
      setConnectWalletModalOpen(false);
    }
  }, [stack, setStack, connectWalletModal.show, navigate, setConnectWalletModalOpen]);

  const hasConnectors = useMemo(() => connectors && connectors.length > 0, [connectors]);

  const availableWallets = useMemo(
    () => (hasConnectors ? connectors : defaultWallets),
    [hasConnectors, connectors],
  );

  if (stack === StackName.PRIVY)
    return (
      <div className="relative flex h-[100vh] w-full items-center justify-center overflow-hidden">
        <PrimaryButton
          onClick={handleNavigateHome}
          text="pages.home"
          className="absolute left-5 top-5"
        />
      </div>
    );

  return (
    <div className="relative flex h-[100vh] w-full items-center justify-center overflow-hidden">
      <PrimaryButton
        onClick={handleNavigateHome}
        text="pages.home"
        className="absolute left-5 top-5"
      />
      <div className="relative flex flex-col items-center gap-5 rounded-[24px] border-[2px] border-border bg-modal-gradient p-6">
        <button
          onClick={handleNavigateHome}
          data-testid="close-connect-wallet-button"
          className="absolute right-4 top-4 transition-transform hover:rotate-180"
        >
          <CloseIcon data-testid="close-icon" className="h-4 w-4" />
        </button>
        <OptimizedImage imageData={wallet} alt="wallet" className="h-[150px] w-[150px]" />
        <p className="gradient-title-gold text-2xl font-bold capitalize leading-none">{t(title)}</p>
        <p className="max-w-[280px] text-center text-md text-white opacity-70">{t(description)}</p>

        <div className="h-[1px] w-full bg-border" />
        <div className="flex w-full justify-center gap-4">
          {availableWallets.map((connector) => (
            <PrimaryButton
              key={connector.name}
              onClick={() => {
                if (stack !== StackName.WAGMI) {
                  setStack(StackName.WAGMI);
                }
                connect(connector.id);
              }}
              svg={
                <img
                  src={connector.icon}
                  alt={connector.name}
                  className="h-8 w-8 sm:h-[56px] sm:w-[56px] sm:min-w-[56px]"
                />
              }
            />
          ))}
          {connectors.length === 0 && (
            <PrimaryButton onClick={() => connect()} text="common.connect" className="w-full" />
          )}
          <PrimaryButton
            onClick={() => {
              if (stack === StackName.PRIVY) {
                login();
                return;
              }
              setStack(StackName.PRIVY);
            }}
            text="common.connectWithEmail"
            className="max-w-[200px]"
            textClassName="!whitespace-normal"
          />
        </div>
      </div>
    </div>
  );
};
